// 계산 원장 — **사용자가 코드를 읽지 않으므로 계산에 창을 낸다**
//
// 이 스텝은 제품 화면을 만들지 않고 순수 함수만 만든다. 그런데 순수 함수만 만들면 이
// 스텝이 남기는 증거가 "테스트가 초록이다" 라는 자기 진술뿐이다. 이 저장소에서 코드를
// 읽는 것은 코딩 에이전트이고 사용자는 읽지 않으므로, 그 진술은 사용자에게 아무것도
// 보여 주지 못한다. 그래서 예외로 페이지 한 장을 만든다.
//
// **시계를 달지 않는다.** 흐르는 숫자 위에서는 정본에 적힌 고정 숫자와 한 자리씩
// 대조할 수 없다. 상단 고정줄도 얹지 않는다 — 그 케이스는 마감 스텝이 소유하고,
// 여기서 흉내 내면 소유하지 않은 것을 소유한 것처럼 보이게 된다.
//
// 이 페이지가 사람에게 물어보는 것은 하나다. **정본에 적힌 숫자와 여기 뜬 숫자가 한
// 자리도 다르지 않은가.**

import type { ReactElement, ReactNode } from 'react'

import {
  ATTACK_NOW,
  DEFENSE_NOW,
  OPTIONS_NOW,
  attackOrder,
  attackRows,
  enemyRows,
  floorRows,
  handleChecks,
  optionRows,
  squadRows,
  type SquadCell,
} from './cases.ts'

const CELL = 'px-3 py-group whitespace-nowrap'
const HEAD = `${CELL} text-left text-xs uppercase text-ink-dim font-normal`

function Section(props: {
  title: string
  lead: string
  children: ReactNode
  testId: string
}): ReactElement {
  return (
    <section className="mt-section rounded border px-4 py-block" aria-label={props.title} data-testid={props.testId}>
      <h2 className="text-base font-semibold">{props.title}</h2>
      <p className="mt-line max-w-3xl text-sm text-ink-dim">{props.lead}</p>
      {props.children}
    </section>
  )
}

function Scroller(props: { children: ReactNode; testId?: string }): ReactElement {
  // 폭이 좁은 폰에서 표가 화면을 밀어내지 않게 표만 가로로 흐르게 한다.
  return (
    <div className="mt-group overflow-x-auto" data-testid={props.testId}>
      {props.children}
    </div>
  )
}

function triggerChip(cell: SquadCell): ReactElement {
  if (cell.status === 'impact-unknown') {
    return <span className="text-ink-mute">착탄 미상 — 대응 없음</span>
  }
  if (cell.status === 'impossible') {
    return (
      <span className="text-rose-300">
        불가 <span>({cell.lateBy} 늦음)</span>
      </span>
    )
  }
  // 칩과 값 사이에 **읽히는 공백**을 넣는다. 여백을 CSS 로만 주면 눈에는 떨어져
  // 보이는데 화면 낭독기에는 `R1:42.0` 처럼 한 덩어리로 간다.
  const [phase, value] = (cell.trigger ?? '').split(' ')
  return (
    <>
      <span className="rounded border px-1.5 py-tight text-xs">{phase}</span>{' '}
      <span className="font-semibold">{value}</span>
      <span className="block text-xs text-ink-mute">{cell.countdown}</span>
    </>
  )
}

export function LedgerScreen(): ReactElement {
  return (
    <main className="mx-auto max-w-5xl px-4 py-block font-mono text-sm">
      <h1 className="text-lg font-semibold">계산 원장</h1>
      <p className="mt-group max-w-3xl font-sans text-sm text-ink-dim">
        타이밍 코어가 내놓는 값을 그대로 찍는다. 여기 뜬 숫자와 요구사항 정의서에 적힌
        숫자가 <strong>한 자리도 다르지 않아야</strong> 이 단계가 끝난 것이다. 시각은
        고정값이라 새로고침해도 바뀌지 않는다 — 문서와 대조할 수 있어야 하기 때문이다.
      </p>

      <Section
        testId="ledger-axis"
        title="1. 착탄 간격은 착탄축이다 — 개시축이 아니다"
        lead={`같은 입력을 두 축으로 읽었다. 위 두 열이 제품이 쓰는 계산이고, 아래 두 열은 틀린 축이다. main 은 두 축에서 같고 sub 에서만 갈라진다. 기준 시각 ${ATTACK_NOW}.`}
      >
        <Scroller>
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className={HEAD} rowSpan={2}>
                  rally
                </th>
                <th className={HEAD} rowSpan={2}>
                  rallying
                </th>
                <th className={HEAD} rowSpan={2}>
                  행군
                </th>
                <th className={HEAD} rowSpan={2}>
                  착탄 간격
                </th>
                <th className={`${HEAD} border-l text-emerald-300`} colSpan={2}>
                  착탄축 (계약)
                </th>
                <th className={`${HEAD} border-l text-rose-300`} colSpan={2}>
                  개시축 (틀린 축)
                </th>
              </tr>
              <tr className="border-b">
                <th className={`${HEAD} border-l`}>개시</th>
                <th className={HEAD}>착탄</th>
                <th className={`${HEAD} border-l`}>개시</th>
                <th className={HEAD}>착탄</th>
              </tr>
            </thead>
            <tbody>
              {attackRows.map((row) => (
                <tr key={row.id} className="border-b" data-testid={`axis-row-${row.id}`}>
                  <td className={CELL}>
                    <div className="font-semibold">{row.label}</div>
                    <div className="font-sans text-xs text-ink-mute">{row.note}</div>
                  </td>
                  <td className={CELL}>{row.rallying}</td>
                  <td className={CELL}>{row.marching}</td>
                  <td className={CELL}>{row.offset}</td>
                  <td className={`${CELL} border-l text-emerald-300`}>{row.impactAxis.open}</td>
                  <td className={`${CELL} text-emerald-300`}>{row.impactAxis.impact}</td>
                  <td className={`${CELL} border-l text-ink-dim`}>{row.openAxis.open}</td>
                  <td className={`${CELL} text-ink-dim`}>{row.openAxis.impact}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Scroller>

        <div className="mt-group rounded border px-3 py-group" data-testid="axis-order">
          <div className="text-xs uppercase text-ink-mute">착탄 순서</div>
          <p className="mt-line">
            <span className="text-emerald-300">착탄축 {attackOrder.impactAxis.join(' → ')}</span>
            <span className="mx-3 text-ink-off">|</span>
            <span className="text-rose-300">개시축 {attackOrder.openAxis.join(' → ')}</span>
          </p>
          <p className="mt-group font-sans text-sm text-ink-dim">
            순서가 통째로 뒤집힌다. 아래쪽이 실패 유형 1번 그 자체다 — 선제해야 할
            strike 가 맨 뒤에 도착하고, 보강해야 할 garrison 이 맨 앞에 도착한다.
            행군 차이(1분)가 착탄 간격(1.0초)보다 크기 때문이고,{' '}
            <strong>행군이 모두 같으면 두 축이 같은 답을 낸다</strong> — 그래서 이
            데이터가 아니면 틀림이 드러나지 않는다.
          </p>
        </div>
      </Section>

      <Section
        testId="ledger-floor"
        title="2. 개시 하한 — 자동 조정과 직접 입력 차단은 다른 일이다"
        lead="조건은 개시 ≥ 지금 + 6초 하나인데 처리가 둘이다. 계산이 어긴 것은 조용히 평행이동하고, 사람이 직접 친 값은 경고와 함께 막는다."
      >
        <Scroller>
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className={HEAD}>상황</th>
                <th className={HEAD}>가장 이른 개시</th>
                <th className={HEAD}>하한</th>
                <th className={HEAD}>평행이동</th>
              </tr>
            </thead>
            <tbody>
              {floorRows.map((row) => (
                <tr key={row.label} className="border-b">
                  <td className={`${CELL} font-sans`}>{row.label}</td>
                  <td className={CELL}>{row.earliestOpen}</td>
                  <td className={CELL}>{row.floor}</td>
                  <td className={`${CELL} ${row.applied ? 'text-amber-300' : 'text-ink-mute'}`}>
                    {row.applied ? `+${row.shift} 전체 이동` : '없음'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Scroller>

        <p className="mt-group max-w-3xl text-sm text-ink-dim">
          코어는 문장을 만들지 않고 거절의 <strong>이름</strong>만 낸다. 그 이름을 자기 언어의
          문장으로 옮기는 것은 화면의 일이다. 그래서 같은 5초도 사람이 친 값이냐 계산으로
          정해진 값이냐에 따라 다른 이름으로 갈린다.
        </p>
        <ul className="mt-group space-y-line">
          {handleChecks.map((check) => (
            <li
              key={`${check.context}-${check.input}`}
              data-testid={`handle-${check.context}-${check.input}`}
            >
              <span className="mr-2 text-ink-mute">
                {check.context === 'handle' ? 'time handle 에 직접 입력' : '계산된 개시 여유'}
              </span>
              <span className="font-semibold">{check.input}</span>
              <span className="mx-2 text-ink-off">→</span>
              {check.accepted ? (
                <span className="text-emerald-300">적용됨 (경계값은 허용한다)</span>
              ) : (
                <span className="text-rose-300">
                  차단 <span className="font-mono text-ink-dim">{check.code}</span>
                </span>
              )}
            </li>
          ))}
        </ul>
      </Section>

      <Section
        testId="ledger-options"
        title="3. rallying 네 옵션을 동시에 본다"
        lead={`진행 중 시퀀스(착탄 원점 12:07:00)에 착탄 간격 +0:30, 행군 2:00 인 rally 를 붙인다. 네 값이 서로 같게 나오면 rallying 을 식에 안 넣은 것이다. 기준 시각 ${OPTIONS_NOW}, 「개시 여유」는 이 시각으로부터 잰 값이다.`}
      >
        <Scroller>
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className={HEAD}>rallying</th>
                <th className={HEAD}>개시</th>
                <th className={HEAD}>개시 여유</th>
                <th className={HEAD}>판정</th>
                <th className={HEAD}>이 옵션의 최소 착탄 간격</th>
              </tr>
            </thead>
            <tbody>
              {optionRows.map((row) => (
                <tr key={row.rallying} className="border-b" data-testid={`option-${row.rallying}`}>
                  <td className={`${CELL} font-semibold`}>{row.rallying}</td>
                  <td className={CELL}>{row.open}</td>
                  <td className={CELL}>{row.lead}</td>
                  <td className={CELL}>
                    {row.feasible ? (
                      <span className="text-emerald-300">가능</span>
                    ) : (
                      <span className="text-rose-300">불가 — confirm 되지 않는다</span>
                    )}
                  </td>
                  <td className={`${CELL} text-ink-dim`}>{row.minimumOffset}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Scroller>
      </Section>

      <Section
        testId="ledger-enemy"
        title="4. 적 rally 착탄 역산 — 앵커가 찍힌 단계에 따라 둘로 갈린다"
        lead={`rallying 앵커는 착탄 = 앵커 + 남은 rallying + 명부의 marching 이고, 행군 앵커는 착탄 = 앵커 + 남은 행군이다. 둘째 식에는 적 marching 항이 없다. 표는 계산된 착탄 순으로 정렬돼 있다. 기준 시각 ${DEFENSE_NOW}.`}
      >
        <Scroller>
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className={HEAD}>적 roster</th>
                <th className={HEAD}>앵커 단계</th>
                <th className={HEAD}>앵커 시각</th>
                <th className={HEAD}>읽은 잔여</th>
                <th className={HEAD}>명부의 marching</th>
                <th className={HEAD}>식에 쓰였나</th>
                <th className={HEAD}>앵커 + </th>
                <th className={HEAD}>착탄</th>
              </tr>
            </thead>
            <tbody>
              {enemyRows.map((row) => (
                <tr key={row.id} className="border-b" data-testid={`enemy-row-${row.id}`}>
                  <td className={`${CELL} font-semibold`}>{row.roster}</td>
                  <td className={CELL}>
                    <span
                      className={`rounded border px-1.5 py-tight text-xs ${
                        row.phase === 'marching' ? 'text-amber-300' : ''
                      }`}
                    >
                      {row.phaseLabel}
                    </span>
                  </td>
                  <td className={CELL}>{row.anchorAt}</td>
                  <td className={CELL}>{row.remaining}</td>
                  <td className={`${CELL} ${row.rosterMarching === '없음' ? 'text-ink-mute' : ''}`}>
                    {row.rosterMarching}
                  </td>
                  <td className={CELL}>
                    {row.usesRosterMarching ? (
                      <span className="text-ink-dim">씀</span>
                    ) : (
                      <span className="text-amber-300">안 씀</span>
                    )}
                  </td>
                  <td className={CELL}>{row.fromAnchor ?? '—'}</td>
                  <td className={`${CELL} font-semibold`}>
                    {row.impact ?? (
                      <span className="font-normal text-ink-mute">착탄 미상(입력필요)</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Scroller>

        <p className="mt-group max-w-3xl font-sans text-sm text-ink-dim">
          Kael 의 두 행을 나란히 보면 개정의 전부가 보인다. 위 행은 명부의 2:00 을 써서
          앵커 + 3:00 이고, 아래 행은 <strong>같은 명부 값을 쓰지 않아</strong> 앵커 +
          0:40 이다. 명부를 썼다면 2:40 이 나온다. Moro 의 두 행도 같은 이야기를 한다 —
          명부가 비어 rallying 앵커는 착탄이 미상인데,{' '}
          <strong>행군 앵커는 명부가 비어 있어도 처음부터 확정된다.</strong> 「착탄 미상」을
          우회하는 유일한 경로가 그것이다. 그리고 정렬을 보면 Nyx 의 rallying 이 Vex 보다
          늦게 끝나는데 착탄은 먼저 온다 — marching 이 짧아 추월한다.
        </p>
      </Section>

      <Section
        testId="ledger-squad"
        title="5. 보낼 시점 — 착탄 + 0.5초 − 내 marching"
        lead="0.5초는 빼는 방향이다. 더하면 squad 가 착탄 0.5초 전에 도착해 가득 찬 castle 에 그냥 return 한다. R 과 M 칩은 roster 가 인게임에서 읽을 카운트다운이 어느 것인지이고, T− 는 앱이 세는 남은 시간이다."
      >
        <Scroller testId="squad-scroller">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className={HEAD}>적 rally (착탄순)</th>
                {squadRows.map((row) => (
                  <th key={row.ally} className={HEAD}>
                    {row.ally}
                    <div className="text-ink-mute">행군 {row.marching}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* 적 rally 를 행으로 둔다. 4절과 행 순서가 같아 위아래로 읽히고, 열이
                  넷뿐이라 데스크톱 폭에서 잘리지 않는다 — 잘리는 열이 하필 「착탄 미상」
                  이면 이 개정의 절반이 기본 폭에서 안 보인다. */}
              {enemyRows.map((enemy) => (
                <tr key={enemy.id} className="border-b" data-testid={`squad-row-${enemy.id}`}>
                  <td className={CELL}>
                    <div className="font-semibold">{enemy.roster}</div>
                    <div className="text-xs text-ink-mute">
                      {enemy.phaseLabel} · 착탄 {enemy.impact ?? '미상'}
                    </div>
                  </td>
                  {squadRows.map((row) => {
                    const cell = row.cells.find((item) => item.enemyId === enemy.id)
                    return (
                      <td key={row.ally} className={CELL}>
                        {cell === undefined ? '—' : triggerChip(cell)}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </Scroller>

        <p className="mt-group max-w-3xl font-sans text-sm text-ink-dim">
          박민수의 열이 전부 <code>M 0:22.0</code> 으로 같은 것이 우연이 아니다. 발사
          순간에 적 착탄까지 남은 시간은 <strong>내 marching − 0.5초로 상수</strong>라,
          내 행군이 적의 어느 행군보다도 짧으면 모든 적 rally에 같은 트리거가 붙는다.
          홍길동은 반대다 — 행군 4:00 이 모든 착탄까지 남은 시간보다 길어 전 조합이
          불가다. 숨기지 않고 얼마나 늦었는지까지 적는 이유는, 그것이 CT 가 배정을 바꿀
          근거이기 때문이다. 이 모드는 「모른다」를 「안다」로 바꾸지{' '}
          <strong>「늦었다」를 「늦지 않았다」로 바꾸지 않는다.</strong>
        </p>
      </Section>

      <p className="mt-section font-sans text-sm">
        <a className="underline" href="/">
          진입 화면으로
        </a>
        <span className="mx-2 text-ink-off">·</span>
        <a className="underline" href="/probe">
          계측 프로브
        </a>
      </p>
    </main>
  )
}
